import React, { useEffect, useState } from 'react';
import { MapPin, Phone, Mail, Clock, Car, Building2, AlertTriangle } from 'lucide-react';
import { FadeInSection } from '../components/FadeInSection';

const PHONE_NUMBER = process.env.REACT_APP_PHONE_NUMBER || '+27210000000';
const PHONE_DISPLAY = PHONE_NUMBER.replace(/(\+27)(\d{2})(\d{3})(\d{4})/, '$1 $2 $3 $4');
const AFTER_HOURS = process.env.REACT_APP_AFTER_HOURS_NUMBER || '+27820000000';
const AFTER_HOURS_DISPLAY = AFTER_HOURS.replace(/(\+27)(\d{2})(\d{3})(\d{4})/, '$1 $2 $3 $4');

export function ContactPage() {
  useEffect(() => {
    document.title = 'Contact | IKhanGynae';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Contact Dr Imraan Khan at IKhanGynae. Consulting at JKD at Melomed Gatesville and Melomed Mitchell\'s Plain, Cape Town. Phone, email, and consulting hours.'
      );
    }
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div>
      {/* ── Hero Banner ── */}
      <section
        className="py-20 px-4 text-white text-center relative overflow-hidden"
        style={{ backgroundColor: 'var(--color-navy)' }}
      >
        <img
          src="/images/logo-symbol-only.png"
          alt=""
          aria-hidden="true"
          className="absolute pointer-events-none select-none"
          style={{ width: 300, right: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.07 }}
        />
        <div className="relative z-10">
          <p
            className="font-heading italic mb-2"
            style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontSize: '1rem' }}
          >
            IKhanGynae, Cape Town Southern Suburbs
          </p>
          <h1
            className="font-heading"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 300,
              fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
              color: '#fff',
            }}
          >
            Contact Us
          </h1>
          <div
            className="mx-auto mt-4"
            style={{ width: 60, height: 2, backgroundColor: 'var(--color-gold)', opacity: 0.7 }}
          />
        </div>
      </section>

      {/* ── Emergency Notice ── */}
      <div
        className="px-4 py-4 border-l-4"
        style={{ backgroundColor: '#FFF8E1', borderLeftColor: '#F59E0B' }}
      >
        <div className="max-w-5xl mx-auto flex items-start gap-3">
          <AlertTriangle className="flex-shrink-0 mt-0.5" size={20} style={{ color: '#D97706' }} />
          <p className="text-sm font-body font-medium" style={{ color: '#78350F', fontFamily: 'var(--font-body)' }}>
            For emergencies, go to your nearest emergency room. For urgent after-hours concerns:{' '}
            <a href={`tel:${AFTER_HOURS}`} className="underline font-bold">{AFTER_HOURS_DISPLAY}</a>.{' '}
            Do NOT use WhatsApp or email for emergencies.
          </p>
        </div>
      </div>

      {/* ── Contact Details + Photo ── */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-10">
            {/* Contact details card */}
            <FadeInSection className="flex-1">
              <div
                className="rounded-xl p-6 shadow-sm h-full"
                style={{ backgroundColor: 'var(--color-cream)' }}
              >
                <h2
                  className="font-heading text-2xl mb-6"
                  style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 400 }}
                >
                  Practice Information
                </h2>
                <ul className="space-y-4 text-sm font-body" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  <li className="flex items-start gap-3">
                    <MapPin size={18} style={{ color: 'var(--color-primary)', marginTop: 2 }} className="flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Consulting Rooms</p>
                      <p className="opacity-70">JKD at Melomed Gatesville &amp; Melomed Mitchell's Plain</p>
                      <p className="opacity-70">Cape Town, Western Cape</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Phone size={18} style={{ color: 'var(--color-primary)', marginTop: 2 }} className="flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Phone</p>
                      <a href={`tel:${PHONE_NUMBER}`} className="hover:underline opacity-80" style={{ color: 'var(--color-primary)' }}>
                        {PHONE_DISPLAY}
                      </a>
                      <p className="opacity-60 mt-0.5 text-xs">
                        After hours:{' '}
                        <a href={`tel:${AFTER_HOURS}`} className="hover:underline" style={{ color: 'var(--color-primary)' }}>{AFTER_HOURS_DISPLAY}</a>
                      </p>
                      <p className="opacity-60 mt-0.5 text-xs">
                        Gatesville: <a href="tel:0216371343" className="hover:underline" style={{ color: 'var(--color-primary)' }}>021 637 1343</a>
                      </p>
                      <p className="opacity-60 mt-0.5 text-xs">
                        Mitchell's Plain: <a href="tel:0213928141" className="hover:underline" style={{ color: 'var(--color-primary)' }}>021 392 8141</a>
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Mail size={18} style={{ color: 'var(--color-primary)', marginTop: 2 }} className="flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Email</p>
                      <a href="mailto:drikhan@ikhangynae.co.za" className="hover:underline opacity-80" style={{ color: 'var(--color-primary)' }}>
                        drikhan@ikhangynae.co.za
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock size={18} style={{ color: 'var(--color-primary)', marginTop: 2 }} className="flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Consulting Hours</p>
                      <p className="opacity-70">Mon – Thu: 08:00 – 17:00</p>
                      <p className="opacity-70">Friday: 08:00 – 13:00</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Building2 size={18} style={{ color: 'var(--color-primary)', marginTop: 2 }} className="flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Hospitals</p>
                      <p className="opacity-70">JKD at Melomed Gatesville</p>
                      <p className="opacity-70">Melomed Mitchell's Plain</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Car size={18} style={{ color: 'var(--color-primary)', marginTop: 2 }} className="flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Parking</p>
                      <p className="opacity-70">Free parking available on-site</p>
                    </div>
                  </li>
                </ul>
              </div>
            </FadeInSection>

            {/* Consulting rooms photo */}
            <FadeInSection delay={0.1} className="flex-1">
              <img
                src="/images/about-office.jpg"
                alt="Dr Khan's consulting rooms"
                className="w-full rounded-2xl shadow-lg object-cover"
                style={{ height: 360 }}
              />
              <p className="text-xs mt-2 text-center font-body opacity-50" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                Dr Khan's consulting rooms
              </p>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ── Medical Aids ── */}
      <section className="py-12 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-5xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading text-xl mb-4"
              style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 400 }}
            >
              Medical Aid Friendly
            </h2>
            <div className="flex flex-wrap gap-3">
              {['Discovery', 'Bonitas', 'Medihelp', 'Momentum', 'Fedhealth'].map((aid) => (
                <span
                  key={aid}
                  className="px-4 py-2 rounded-full text-sm font-body font-medium"
                  style={{
                    backgroundColor: 'var(--color-primary-light)',
                    color: 'var(--color-primary)',
                    fontFamily: 'var(--font-body)',
                    border: '1px solid var(--color-primary)',
                  }}
                >
                  {aid}
                </span>
              ))}
            </div>
            <p className="text-xs mt-3 font-body opacity-50" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
              Dr Khan is registered with most major medical aids. If yours isn't listed above, please contact the practice — we'll confirm your cover.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ── Map ── */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading text-xl mb-4"
              style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 400 }}
            >
              Find Us
            </h2>
            <div className="rounded-2xl overflow-hidden shadow-md">
              {/* TODO: replace with Google Maps when API key is available */}
              <iframe
                title="IKhanGynae location map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=18.6115%2C-34.0626%2C18.6515%2C-34.0226&layer=mapnik&marker=-34.0426%2C18.6315"
                width="100%"
                height="350"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
              />
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ── Contact Form ── */}
      <section className="py-16 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-2xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading text-2xl mb-6"
              style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 400 }}
            >
              Send Us a Message
            </h2>
            {submitted ? (
              <div
                className="rounded-xl p-6 text-center"
                style={{ backgroundColor: 'var(--color-primary-light)', border: '1px solid var(--color-primary)' }}
              >
                <p className="font-heading text-xl mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)' }}>
                  Thank you for your message!
                </p>
                <p className="text-sm font-body opacity-70" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  We'll be in touch as soon as possible.
                </p>
                <button
                  className="mt-4 text-sm font-body underline font-medium"
                  style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-body)' }}
                  onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', message: '' }); }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 bg-white rounded-xl p-6 shadow-sm">
                <div>
                  <label className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }} htmlFor="c-name">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="c-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-body focus:outline-none focus:ring-2 bg-white"
                    style={{ fontFamily: 'var(--font-body)' }}
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }} htmlFor="c-email">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="c-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-body focus:outline-none focus:ring-2 bg-white"
                    style={{ fontFamily: 'var(--font-body)' }}
                    placeholder="you@email.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }} htmlFor="c-phone">
                    Phone Number <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    id="c-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-body focus:outline-none focus:ring-2 bg-white"
                    style={{ fontFamily: 'var(--font-body)' }}
                    placeholder="+27 xx xxx xxxx"
                  />
                </div>
                <div>
                  <label className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }} htmlFor="c-message">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="c-message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-body focus:outline-none focus:ring-2 resize-none bg-white"
                    style={{ fontFamily: 'var(--font-body)' }}
                    placeholder="Your message..."
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 rounded-lg font-body font-semibold text-sm text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: 'var(--color-primary)', fontFamily: 'var(--font-body)' }}
                >
                  Send Message
                </button>
              </form>
            )}
          </FadeInSection>
        </div>
      </section>
    </div>
  );
}
